import { useEffect, useState } from 'react'
import { getWeekStart, toDateStr } from './utils'

function parseInitialState() {
  const sp = new URLSearchParams(window.location.search)
  const now = new Date()
  return { sp, now }
}

export function useCalendarUrlState() {
  const [now] = useState(() => new Date())

  const [viewMode, setViewMode] = useState<'month' | 'week'>(() => {
    const { sp } = parseInitialState()
    return sp.get('v') === 'month' ? 'month' : 'week'
  })
  const [year, setYear] = useState(() => {
    const { sp, now } = parseInitialState()
    return Number(sp.get('y')) || now.getFullYear()
  })
  const [month, setMonth] = useState(() => {
    const { sp, now } = parseInitialState()
    return Number(sp.get('m')) || now.getMonth() + 1
  })
  const [weekStart, setWeekStart] = useState<Date>(() => {
    const { sp, now } = parseInitialState()
    const w = sp.get('w')
    if (w) {
      const d = new Date(w)
      d.setHours(0, 0, 0, 0)
      return d
    }
    return getWeekStart(now)
  })
  const [selectedInstitutionIds, setSelectedInstitutionIds] = useState<string[] | null>(() => {
    const { sp } = parseInitialState()
    const s = sp.get('s')
    if (s === null) return null
    return s === '' ? [] : s.split(',')
  })
  const [startHour, setStartHour] = useState(() => {
    const { sp } = parseInitialState()
    return sp.has('sh') ? Number(sp.get('sh')) : 0
  })
  const [endHour, setEndHour] = useState(() => {
    const { sp } = parseInitialState()
    return sp.has('eh') ? Number(sp.get('eh')) : 23
  })

  // Sync state → URL (replaceState so it doesn't pollute browser history)
  useEffect(() => {
    const sp = new URLSearchParams()
    if (viewMode !== 'month') sp.set('v', viewMode)
    if (viewMode === 'week') {
      sp.set('w', toDateStr(weekStart))
    } else {
      sp.set('y', String(year))
      sp.set('m', String(month))
    }
    if (startHour !== 0) sp.set('sh', String(startHour))
    if (endHour !== 23) sp.set('eh', String(endHour))
    if (selectedInstitutionIds !== null) sp.set('s', selectedInstitutionIds.join(','))
    const search = sp.toString()
    history.replaceState(null, '', search ? `?${search}` : window.location.pathname)
  }, [viewMode, year, month, weekStart, startHour, endHour, selectedInstitutionIds])

  return {
    now,
    viewMode,
    setViewMode,
    year,
    setYear,
    month,
    setMonth,
    weekStart,
    setWeekStart,
    selectedInstitutionIds,
    setSelectedInstitutionIds,
    startHour,
    setStartHour,
    endHour,
    setEndHour,
  }
}
